import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { Icon } from "@/lib/icons";
import { useAuth } from "@/context/AuthContext";
import { buildRail } from "@/lib/navLayout";

// Top-bar cross-module search: members, partners, visitors, events, meetings, grants.
export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const { me, setPersona, persona } = useAuth();
  const ql = q.trim().toLowerCase();
  const pages = ql.length < 2 ? [] : buildRail(me?.nav || [], { isSuper: !!me?.user?.is_super_admin && !persona })
    .flatMap((g) => g.items.map((i) => ({ ...i, group: g.label }))).filter((i) => i.label.toLowerCase().includes(ql)).slice(0, 6);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    setOpen(true);
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => { setRes(r.data.results || []); setOpen(true); }).catch(() => setRes([])), 220);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const openPage = (p) => {
    setOpen(false); setQ("");
    if (p.persona) setPersona(p.persona);
    else window.dispatchEvent(new CustomEvent("rca:navigate", { detail: { section: p.target } }));
  };
  const go = (r) => {
    if (r.section === "membership_directory" && r.id) sessionStorage.setItem("rca_open_member", r.id);
    window.dispatchEvent(new CustomEvent("rca:navigate", { detail: { section: r.section } }));
    setOpen(false); setQ("");
  };
  return (
    <div ref={box} className="relative flex-1 min-w-0 max-w-md" data-testid="global-search">
      <div className="flex items-center gap-2 bg-warm-cream border border-ink/15 rounded-lg px-3 min-h-[42px]">
        <Icon name="Search" className="w-4 h-4 text-ink/40" />
        <input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => (res.length || pages.length) && setOpen(true)}
          placeholder="Search pages and members…" className="bg-transparent flex-1 text-[14px] py-2 outline-none" />
        {q && <button data-testid="global-search-clear" onClick={() => { setQ(""); setRes([]); }} className="text-ink/40 hover:text-ink"><Icon name="X" className="w-4 h-4" /></button>}
      </div>
      {open && q.trim().length >= 2 && (
        <div data-testid="global-search-results" className="absolute z-30 left-0 right-0 mt-1 bg-cream border border-ink/15 rounded-lg shadow-lg max-h-[60vh] overflow-y-auto">
          {pages.map((p) => (
            <button key={`page-${p.id}`} data-testid={`global-search-page-${p.id}`} onClick={() => openPage(p)} className="w-full text-left px-3 py-2 border-b border-ink/8 hover:bg-warm-cream/60">
              <div className="text-[14px] font-semibold">{p.label}</div>
              <div className="text-[12px] text-ink/50">Page · {p.group}</div>
            </button>
          ))}
          {res.length === 0 && pages.length === 0 ? <div className="px-3 py-3 text-[13px] text-ink/50">No matches for "{q}".</div>
            : res.map((r, i) => (
              <button key={i} data-testid={`global-search-result-${i}`} onClick={() => go(r)} className="w-full text-left px-3 py-2 border-b border-ink/8 last:border-0 hover:bg-warm-cream/60">
                <div className="text-[14px] font-semibold">{r.label || "—"}</div>
                <div className="text-[12px] text-ink/50">{r.type}{r.sub ? ` · ${r.sub}` : ""}</div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
